import React, { useMemo } from 'react';
import { Box, useTheme, Typography, keyframes, alpha } from '@mui/material';

// Varredura suave do radar
const sweep = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// Pulso suave dos blips no radar
const blipPulse = keyframes`
  0%, 100% { opacity: 0.2; transform: scale(0.8); }
  50%       { opacity: 1;   transform: scale(1.1); }
`;

const RadarToChartAnimation = ({ jobStatus, isMobile }) => {
  const theme = useTheme();

  const BRAND_COLOR = '#6fa84f';        // verde brand
  const BRAND_GLOW  = alpha(BRAND_COLOR, 0.35);
  const BRAND_DIM   = alpha(BRAND_COLOR, 0.18);
  const GRID_LINE   = alpha(theme.palette.common.white, 0.06);

  const MAX_PARTICLES = 50;
  const VISUAL_COLS   = 5;
  const colHeights    = [4, 7, 10, 13, 16]; // soma = 50

  // Geometria global
  const containerWidth  = isMobile ? 340 : 560;
  const containerHeight = 190;

  // Partículas
  const particleW    = isMobile ? 20 : 26;
  const particleH    = isMobile ? 4  : 6;
  const colGap       = 5;
  const chartWidth   = VISUAL_COLS * particleW + (VISUAL_COLS - 1) * colGap;
  const chartBaseY   = containerHeight - 28;

  // Centrar bloco (radar + gap + gráfico) no container
  // S = (W - 2R - gap - CW) / 3  →  garante que margem esq = margem dir
  const radarRadius  = isMobile ? 58 : 70;
  const gap          = isMobile ? 20 : 40;
  const contentStart = (containerWidth - 2 * radarRadius - gap - chartWidth) / 3;
  const radarCX      = contentStart + radarRadius;
  const chartLeft    = contentStart + 2 * radarRadius + gap;
  const chartOriginX = (chartLeft + containerWidth - chartWidth) / 2;

  // radarCY alinhado ao centro visual das barras
  const maxBarH      = colHeights[4] * (particleH + 2);
  const radarCY      = chartBaseY - maxBarH / 2;

  // Pré-computar posições de cada partícula
  const particles = useMemo(() => {
    const arr = [];
    let idx = 0;
    for (let col = 0; col < VISUAL_COLS; col++) {
      for (let row = 0; row < colHeights[col]; row++) {
        const r     = Math.random() * (radarRadius - 14);
        const theta = Math.random() * 2 * Math.PI;
        arr.push({
          id:          idx++,
          targetCol:   col,
          rowInCol:    row,
          radarX:      r * Math.cos(theta),
          radarY:      r * Math.sin(theta),
          animDuration: 1.4 + Math.random() * 1.8,
          animDelay:   -Math.random() * 3,
        });
      }
    }
    return arr.sort(() => Math.random() - 0.5);
  }, []);

  const percentage = jobStatus.percentage || 0;
  const isFinished = percentage >= 100 || jobStatus.status === 'completed';

  const colStructuredCounts = useMemo(() => {
    return colHeights.map((capacity, col) => {
      const min = col * 20;
      const max = (col + 1) * 20;
      if (percentage <= min) return 0;
      if (percentage >= max) return capacity;
      return Math.floor(((percentage - min) / 20) * capacity);
    });
  }, [percentage]);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2 }}>
      <Box sx={{ position: 'relative', width: containerWidth, height: containerHeight }}>

        {/* ── RADAR (lado esquerdo, ~40%) ── */}
        <Box
          sx={{
            position: 'absolute',
            left: radarCX - radarRadius,
            top:  radarCY - radarRadius,
            width:  radarRadius * 2,
            height: radarRadius * 2,
            borderRadius: '50%',
            border: `1px solid ${alpha(BRAND_COLOR, 0.3)}`,
            background: `radial-gradient(circle, ${BRAND_DIM} 0%, transparent 70%)`,
            overflow: 'hidden',
            opacity: isFinished ? 0 : 1,
            transform: isFinished ? 'scale(0.6)' : 'scale(1)',
            transition: 'opacity 0.9s ease, transform 0.9s cubic-bezier(0.68,-0.55,0.27,1.55)',
          }}
        >
          {/* Anéis concêntricos */}
          {[0.5, 0.75].map((ratio) => (
            <Box key={ratio} sx={{
              position: 'absolute',
              left: `${(1 - ratio) * 50}%`,
              top:  `${(1 - ratio) * 50}%`,
              width:  `${ratio * 100}%`,
              height: `${ratio * 100}%`,
              borderRadius: '50%',
              border: `1px solid ${alpha(BRAND_COLOR, 0.15)}`,
              pointerEvents: 'none',
            }} />
          ))}

          {/* Crosshair */}
          {['vertical', 'horizontal'].map((dir) => (
            <Box key={dir} sx={{
              position: 'absolute',
              ...(dir === 'vertical'
                ? { left: '50%', top: 0, bottom: 0, width: '1px' }
                : { top: '50%', left: 0, right: 0, height: '1px' }),
              background: alpha(BRAND_COLOR, 0.2),
            }} />
          ))}

          {/* Scanner sweep */}
          <Box sx={{
            width: '100%', height: '100%', borderRadius: '50%',
            background: `conic-gradient(from 0deg, transparent 65%, ${alpha(BRAND_COLOR, 0.6)} 100%)`,
            animation: `${sweep} 2.8s linear infinite`,
            transformOrigin: 'center center',
          }} />
        </Box>

        {/* Label "Radar" */}
        {!isFinished && (
          <Typography variant="caption" sx={{
            position: 'absolute',
            left: radarCX,
            top: radarCY + radarRadius + 6,
            transform: 'translateX(-50%)',
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: 0.3,
            color: alpha(BRAND_COLOR, 0.55),
          }}>
            Capturando
          </Typography>
        )}

        {/* ── GRÁFICO (lado direito, ~60%) ── */}

        {/* Grid removido a pedido do usuário */}

        {/* Rótulos de % acima de cada coluna */}
        {Array.from({ length: VISUAL_COLS }).map((_, col) => {
          const min = col * 20;
          const max = (col + 1) * 20;
          let display = null;
          let isCurrent = false;

          if (percentage >= max) { display = max; }
          else if (percentage > min) { display = percentage; isCurrent = true; }
          else if (percentage === min && col === 0) { display = 0; isCurrent = true; }

          if (display === null) return null;

          const tx = chartOriginX + col * (particleW + colGap) + particleW / 2;
          const ty = chartBaseY - colHeights[col] * (particleH + 2) - 16;

          return (
            <Typography key={`lbl-${col}`} sx={{
              position: 'absolute',
              left: tx,
              top:  ty,
              transform: 'translateX(-50%)',
              fontSize: isCurrent ? '0.72rem' : '0.6rem',
              fontWeight: isCurrent ? 900 : 500,
              color: isCurrent ? BRAND_COLOR : alpha(theme.palette.text.secondary, 0.6),
              filter: isCurrent ? `drop-shadow(0 0 5px ${BRAND_GLOW})` : 'none',
              transition: 'all 0.3s ease',
              lineHeight: 1,
              zIndex: 20,
            }}>
              {display}%
            </Typography>
          );
        })}

        {/* Texto de progresso abaixo do gráfico */}
        <Box sx={{
          position: 'absolute',
          left: chartOriginX + chartWidth / 2,
          top: chartBaseY + 8,
          transform: 'translateX(-50%)',
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}>
          <Typography sx={{ fontSize: '0.7rem', color: alpha(BRAND_COLOR, 0.55), fontWeight: 600, letterSpacing: 0.3 }}>
            <span style={{ color: BRAND_COLOR, fontWeight: 800 }}>{jobStatus.completed}</span>
            {' / '}{jobStatus.total} processados
          </Typography>
        </Box>

        {/* ── PARTÍCULAS — blips no radar → barras no gráfico ── */}
        {particles.map((p) => {
          const isStructured = p.rowInCol < colStructuredCounts[p.targetCol];

          const startX = radarCX + p.radarX - particleW / 2;
          const startY = radarCY + p.radarY - particleH / 2;
          const endX   = chartOriginX + p.targetCol * (particleW + colGap);
          const endY   = chartBaseY   - p.rowInCol  * (particleH + 2);

          const cx = isStructured ? endX : startX;
          const cy = isStructured ? endY : startY;

          return (
            <Box key={p.id} sx={{
              position: 'absolute',
              top: 0, left: 0,
              width: particleW,
              height: particleH,
              transform: `translate(${cx}px, ${cy}px) scale(${isStructured ? 1 : 0.35})`,
              transition: 'transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1)',
              zIndex: isStructured ? 10 : 2,
            }}>
              <Box sx={{
                width: '100%', height: '100%',
                backgroundColor: isStructured ? BRAND_COLOR : theme.palette.success.main,
                borderRadius: isStructured ? '3px' : '50%',
                boxShadow: isStructured
                  ? `0 0 6px ${alpha(BRAND_COLOR, 0.45)}, 0 1px 0 ${alpha('#fff', 0.08)}`
                  : `0 0 7px ${theme.palette.success.main}`,
                opacity: isStructured ? 1 : 0.75,
                transition: 'background-color 0.5s, border-radius 0.5s, box-shadow 0.5s',
                animation: isStructured
                  ? 'none'
                  : `${blipPulse} ${p.animDuration}s infinite ease-in-out ${p.animDelay}s`,
              }} />
            </Box>
          );
        })}

      </Box>
    </Box>
  );
};

export default RadarToChartAnimation;
